import { registerMutation, useMutation } from 'data-fetching-client';
import { ViewDraft } from '../model/viewDraft';
import { useClientStorage, useDraftContext, useBypassStorage } from '../data/VizCoreProvider';
import { useMemo } from 'react';
import { getVizSettingsQueryInternal } from './useVizSettings';
import { useViewMeta } from './useViewMeta';
import { useView } from './useView';
import { ViewAPI } from '../data/viewAPI';

// Use a distinct fieldName for viz settings mutations
export const mutateVizSettingsMutation = registerMutation({
  fieldName: 'vizcoreVizSettings',
  fetcher: async ({
    vizSetting,
    draftContext,
    clientStorage,
    bypassStorage,
    shouldLegacyAutoSaveForTable,
    view
  }) => {
    var _draftContext$editHas, _existingDraft$vizSet;
    // Auto-save table view to backend regardless of bypass mode
    if (shouldLegacyAutoSaveForTable && view) {
      await ViewAPI.legacyPutViewDefault(vizSetting.toBEView(view)).catch(e => {
        console.error('Failed to automatically save view', e);
      });
    }

    // QUICK FIX: Skip storage operations to prevent rollbacks
    if (bypassStorage) {
      return {
        vizSettingsHash: 'bypass-mode',
        viewDraftHash: draftContext.editHash || 'bypass-draft-active',
        vizSetting
      };
    }

    // Save viz setting and get hash
    const vizSettingsHash = await vizSetting.toClientCache(clientStorage);

    // Load and update view draft reference
    const existingDraft = await ViewDraft.fromClientCache((_draftContext$editHas = draftContext.editHash) !== null && _draftContext$editHas !== void 0 ? _draftContext$editHas : '',
    // there is no empty string hash, so effectively existingDraft will be null
    clientStorage).catch(() => null);

    // Create or update view draft with new viz settings hash
    const baseDraft = existingDraft !== null && existingDraft !== void 0 ? existingDraft : new ViewDraft({
      viewId: draftContext.viewId
    });
    const updatedDraft = baseDraft.with({
      vizSettingHashesById: Object.assign({}, (_existingDraft$vizSet = existingDraft === null || existingDraft === void 0 ? void 0 : existingDraft.vizSettingHashesById) !== null && _existingDraft$vizSet !== void 0 ? _existingDraft$vizSet : {}, {
        [vizSetting.visualizationType]: vizSettingsHash
      })
    });

    // Save updated view draft
    const viewDraftHash = await updatedDraft.toClientCache(clientStorage);
    return {
      vizSettingsHash,
      viewDraftHash,
      vizSetting
    };
  },
  args: ['vizSetting', 'draftContext', 'clientStorage', 'bypassStorage', 'shouldLegacyAutoSaveForTable', 'view']
});
const EMPTY_OBJECT = {};

/**
 * Hook to save and manage viz settings drafts with auto-batching.
 *
 * This hook is built on Data Fetching Client (DFC) but provides a cleaner API
 * for viz settings mutations. It handles all the complexity of saving viz settings drafts,
 * managing the edit hash, and updating client-side storage.
 *
 * ## Auto-Batching and Debouncing
 *
 * Multiple save operations within the debounce window (default 100ms) will be batched
 * into a single save operation. This is especially useful for rapid UI changes like:
 * - Dragging column widths
 * - Typing in search boxes
 * - Rapid filter changes
 *
 * ## Draft Persistence
 *
 * When viz settings are modified with this hook:
 *
 * 1. The updated viz setting is saved to client-side storage with a unique hash
 * 2. A composite representation of the persisted view draft is updated with the new viz settings hash
 * 3. The editHash is automatically updated in context. This is equivalent to the "version key" of a form.
 *
 * This creates a complete draft cycle, with useVizSettings reading from the same draft that
 * useMutateVizSettings writes to.
 *
 * Note that this does not actually save to backend, it only persists to client-side storage.
 * In order to save to backend, you need to use the `useSaveViewDraft` hook.
 *
 * ## Integration with useVizSettings
 *
 * This hook is designed to work seamlessly with useVizSettings:
 *
 * ```tsx
 * // Read viz settings data
 * const { data: vizSettings } = useVizSettings('table');
 *
 * // Write viz settings data
 * const [saveVizSettings] = useMutateVizSettings();
 * ```
 *
 * ## Basic Usage
 *
 * ```tsx
 * const [saveVizSettings, { loading }, flushSave] = useMutateVizSettings({
 *   onSuccess: (vizSetting) => {
 *     console.log('Saved viz settings', vizSetting);
 *   },
 *   onError: (error) => {
 *     console.error('Failed to save viz settings', error);
 *   },
 *   autoBatch: 200 // Custom debounce time
 * });
 *
 * // Update existing viz settings
 * saveVizSettings(vizSettings.with({
 *   columns: ['name', 'amount'],
 *   numberOfFrozenColumns: 2
 * }));
 *
 * // Or use updater function pattern
 * saveVizSettings(prev => prev?.with({ columns: newColumns }));
 *
 * // Manual flush if needed
 * await flushSave();
 * ```
 *
 * ## Handling Multiple Properties
 *
 * Use the `with()` method to update multiple properties at once:
 *
 * ```tsx
 * saveVizSettings(vizSettings.with({
 *   columns: ['name', 'amount', 'closedate'],
 *   numberOfFrozenColumns: 3,
 *   sortBy: 'amount'
 * }));
 * ```
 *
 * ## Working with Promises
 *
 * The saveVizSettings function returns a Promise that resolves when the save is complete:
 * You can either use it directly, or use the `onSuccess` and `onError` callbacks available on the hook options.
 *
 * ```tsx
 * saveVizSettings(updatedVizSettings)
 *   .then(result => {
 *     // Handle successful save
 *     console.log('Viz settings saved:', result);
 *   })
 *   .catch(error => {
 *     // Handle save error
 *     console.error('Save failed:', error);
 *   });
 * ```
 *
 * ## Best Practices
 *
 * 1. **Always use viz setting's `with()` method for immutable updates:**
 *    ```tsx
 *    // Good - creates a new VizSetting object with updated properties
 *    saveVizSettings(vizSettings.with({ columns: newColumns }));
 *
 *    // Bad - mutating the viz settings directly
 *    vizSettings.settings.columns = newColumns;
 *    saveVizSettings(vizSettings);
 *    ```
 *
 * 2. **Handle loading state for better UX:**
 *    ```tsx
 *    const [saveVizSettings, { loading }] = useMutateVizSettings();
 *
 *    return (
 *      <Button
 *        onClick={() => saveVizSettings(updatedVizSettings)}
 *        disabled={loading}
 *      >
 *        {loading ? 'Saving...' : 'Save'}
 *      </Button>
 *    );
 *    ```
 *
 * 3. **Use appropriate debounce times:**
 *    ```tsx
 *    // For rapid changes like column resizing
 *    const [saveVizSettings] = useMutateVizSettings({ autoBatch: 300 });
 *
 *    // For immediate feedback
 *    const [saveVizSettings] = useMutateVizSettings({ autoBatch: 0 });
 *    ```
 *
 * For more details about the VizSetting model, see the VizSetting class JSDoc
 * in core/model/vizSetting.ts.
 *
 * @param options Optional callbacks for success and error handling, plus debounce configuration
 * @see VizSetting
 * @see useVizSettings
 * @returns [saveVizSettings function, { loading, error }, flushSave function]
 */
export function useMutateVizSettings(options = EMPTY_OBJECT) {
  const [draftContext, onDraftChange] = useDraftContext();
  const clientStorage = useClientStorage();
  const bypassStorage = useBypassStorage();
  const {
    data: viewMeta
  } = useViewMeta();
  const {
    data: {
      vizcoreCurrentView: view
    } = {}
  } = useView();
  const memoizedOptions = useMemo(() => {
    const {
      onError,
      onSuccess
    } = options;
    return {
      onError,
      onCompleted: ({
        vizcoreVizSettings: {
          viewDraftHash,
          vizSetting
        }
      }) => {
        // QUICK FIX: Skip edit hash update to prevent rollbacks
        if (bypassStorage) {
          if (draftContext.editHash === null) {
            onDraftChange(Object.assign({}, draftContext, {
              editHash: viewDraftHash
            }));
          }
        } else {
          onDraftChange(Object.assign({}, draftContext, {
            editHash: viewDraftHash
          }));
        }
        onSuccess === null || onSuccess === void 0 || onSuccess(vizSetting);
      }
    };
  }, [options, onDraftChange, draftContext, bypassStorage]);
  const [mutate, result] = useMutation(mutateVizSettingsMutation, Object.assign({}, memoizedOptions, {
    optimisticResponse: vars => {
      return {
        vizcoreVizSettings: {
          vizSettingsHash: 'bypass-draft-active',
          viewDraftHash: vars.draftContext.editHash,
          vizSetting: vars.vizSetting
        }
      };
    },
    update: (cache, {
      data
    }) => {
      // Skip update for optimistic responses

      if (data !== null && data !== void 0 && data.vizcoreVizSettings && view) {
        cache.writeQuery({
          query: getVizSettingsQueryInternal,
          variables: {
            draftContext: Object.assign({}, draftContext, {
              editHash: data.vizcoreVizSettings.viewDraftHash
            }),
            clientStorage,
            vizType: data.vizcoreVizSettings.vizSetting.visualizationType,
            view // View dependency handled by useView hook
          },
          data: {
            vizcoreVizSettings: data.vizcoreVizSettings.vizSetting
          }
        });
      }
    }
  }));

  // Create a function to perform the actual mutation
  const performMutation = useMemo(() => vizSetting => {
    // Precompute shouldLegacyAutoSaveForTable to avoid passing viewMeta as fetcher dependency
    const shouldLegacyAutoSaveForTable = (viewMeta === null || viewMeta === void 0 ? void 0 : viewMeta.isHubSpotDefined) && view && view.namespaceName === 'NONE' && options.source !== 'queryParam' && vizSetting.visualizationType === 'TABLE';
    return mutate({
      variables: {
        vizSetting,
        draftContext,
        clientStorage,
        bypassStorage,
        shouldLegacyAutoSaveForTable,
        view
      }
    }).then(r => {
      var _r$data;
      return (_r$data = r.data) === null || _r$data === void 0 ? void 0 : _r$data.vizcoreVizSettings.vizSetting;
    });
  }, [mutate, draftContext, clientStorage, bypassStorage, viewMeta, view, options.source]);
  return useMemo(() => [performMutation, result], [performMutation, result]);
}